import classNames from "classnames";
import React, {
  useCallback,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type PropsWithChildren
} from "react";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from "../../../../Shared/Buttons/React";
import { Checkbox, CheckboxColor, CheckboxVariant } from "../../Checkbox/React";

interface SearchInputProps {
  name?: string;
  placeholder?: string;
  onSubmit?: (value?: string) => void;
  onInput?: (value?: string) => void;
  onChange?: (value?: string) => void;
  additionalClassName?: string;
  filterCategories?: string[];
  onFilterChange?: (selected: string[]) => void;
}

export const SearchInput = ({
  children,
  name,
  placeholder,
  additionalClassName,
  onSubmit,
  onChange,
  onInput,
  filterCategories = [],
  onFilterChange
}: PropsWithChildren<SearchInputProps>) => {
  const [value, setValue] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      setValue(evt.currentTarget.value);

      if (evt.key == "13" || evt.key == "Enter") {
        if (!!onChange) {
          onChange(evt.currentTarget.value);
        }

        if (!!onSubmit) {
          onSubmit(evt.currentTarget.value);
        }
      }
    },
    [onChange, onSubmit]
  );

  const handleInput = useCallback(
    (evt: FormEvent<HTMLInputElement>) => {
      setValue(evt.currentTarget.value);
      if (!!onInput) {
        onInput(evt.currentTarget.value);
      }
    },
    [onInput]
  );

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setValue(evt.currentTarget.value);

      if (!!onChange) {
        onChange(evt.currentTarget.value);
      }
    },
    [onChange]
  );

  // Handle filter checkbox change
  const handleFilterChange = useCallback(
    (category: string, checked: boolean) => {
      setSelectedFilters(prev => {
        const updated = checked ? [...prev, category] : prev.filter(c => c !== category);
        if (onFilterChange) {
          onFilterChange(updated);
        }

        return updated;
      });
    },
    [onFilterChange]
  );

  return (
    <div>
      {filterCategories.length > 1 && (
        <div className="overflow-x-auto py-xs">
          <div className="flex flex-nowrap gap-x-md gap-y-xs min-w-0">
            {filterCategories.map(category => (
              <div key={category} className="flex items-center">
                <Checkbox
                  checked={selectedFilters.includes(category)}
                  onChange={checked => handleFilterChange(category, checked)}
                  label={category}
                  variant={CheckboxVariant.Element}
                  color={CheckboxColor.Default}
                  additionalClassName="whitespace-nowrap"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <label
        className={classNames(
          "flex items-center p-xs gap-xs rounded-tl-br-md bg-neutral-0 transition-short-in-out",
          "border hover:border-dark-blue",
          "focus-outline has-[input:focus-visible]:outline",
          "border-neutral-300 has-[input:focus-visible]:border-dark-blue",
          additionalClassName
        )}
      >
        <input
          className={classNames(
            "w-full p-sm text-t-sm placeholder-t-neutral-700 text-dark-blue",
            "focus:[outline-style:none] order-1",
            "rounded-tl-br-sm",
            "[&::-webkit-search-cancel-button]:appearance-none",
            "[&::-webkit-search-cancel-button]:w-[1.125rem] [&::-webkit-search-cancel-button]:h-[1.125rem]",
            "[&::-webkit-search-cancel-button]:bg-[url(/svg/clear.svg)]",
            "[&::-webkit-search-cancel-button]:cursor-pointer",
            "peer/search-input"
          )}
          onInput={handleInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          name={name}
          placeholder={placeholder}
          type="search"
          value={value}
          autoComplete="off"
        />
        {children}
        <Button
          additionalClass="flex-none order-3 [&_svg]:fill-dark-blue peer-focus-visible/search-input:[&_svg]:fill-white peer-focus-visible/search-input:bg-dark-blue"
          icon="/dist/sprite.svg#sys-search"
          variant={ButtonVariant.Square}
          title={"search"}
          size={ButtonSize.MD}
          onClick={() => {
            if (!!onSubmit) {
              onSubmit(value);
            }
          }}
          color={ButtonColor.Neutral}
          type={ButtonType.Submit}
        />
      </label>
    </div>
  );
};

export default SearchInput;
